import React, { memo } from 'react';
import {
  LineChart,
  BarChart,
  PieChart,
  AreaChart,
  ScatterChart,
  Line,
  Bar,
  Pie,
  Area,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { validateChartSpec } from './chartUtils';
import './ChartRenderer.css';

// Default colors for chart series
const DEFAULT_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
  '#d084d0', '#ffb347', '#a4de6c', '#d0ed57', '#83a6ed'
];

// Pie chart specific colors
const PIE_COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1', '#D084D0'
];

/**
 * Renders a Line Chart
 */
const LineChartRenderer = memo(({ data, xAxis, series, title }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxis} />
        <YAxis />
        <Tooltip />
        <Legend />
        {series.map((s, idx) => (
          <Line
            key={idx}
            type="monotone"
            dataKey={s.dataKey}
            stroke={s.stroke || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
            name={s.name || s.dataKey}
            strokeWidth={2}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
});

LineChartRenderer.displayName = 'LineChartRenderer';

/**
 * Renders a Bar Chart
 */
const BarChartRenderer = memo(({ data, xAxis, series, title }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxis} />
        <YAxis />
        <Tooltip />
        <Legend />
        {series.map((s, idx) => (
          <Bar
            key={idx}
            dataKey={s.dataKey}
            fill={s.fill || s.stroke || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
            name={s.name || s.dataKey}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
});

BarChartRenderer.displayName = 'BarChartRenderer';

/**
 * Renders a Pie Chart
 */
const PieChartRenderer = memo(({ data, title }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
});

PieChartRenderer.displayName = 'PieChartRenderer';

/**
 * Renders an Area Chart
 */
const AreaChartRenderer = memo(({ data, xAxis, series, title }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxis} />
        <YAxis />
        <Tooltip />
        <Legend />
        {series.map((s, idx) => (
          <Area
            key={idx}
            type="monotone"
            dataKey={s.dataKey}
            stroke={s.stroke || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
            fill={s.fill || s.stroke || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
            name={s.name || s.dataKey}
            fillOpacity={0.6}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
});

AreaChartRenderer.displayName = 'AreaChartRenderer';

/**
 * Renders a Scatter Chart
 */
const ScatterChartRenderer = memo(({ data, xAxis, series, title }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxis} name={xAxis} />
        <YAxis dataKey={series[0]?.dataKey} name={series[0]?.name || series[0]?.dataKey} />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
        <Legend />
        {series.map((s, idx) => (
          <Scatter
            key={idx}
            name={s.name || s.dataKey}
            data={data}
            fill={s.fill || s.stroke || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
          />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  );
});

ScatterChartRenderer.displayName = 'ScatterChartRenderer';

/**
 * Main ChartRenderer component
 * Receives a chart specification and renders the appropriate chart type
 */
export const ChartRenderer = memo(({ chartSpec }) => {
  // Validate the chart specification
  const validation = validateChartSpec(chartSpec);
  
  if (!validation.valid) {
    return (
      <div className="chart-renderer-error">
        <div className="chart-renderer-error-title">⚠️ Invalid Chart Specification</div>
        <ul className="chart-renderer-error-list">
          {validation.errors.map((error, idx) => (
            <li key={idx}>{error}</li>
          ))}
        </ul>
      </div>
    );
  }
  
  const { type, title, data, xAxis, series } = chartSpec;
  const chartType = type.toLowerCase();
  
  return (
    <div className="chart-renderer-container">
      {title && <div className="chart-renderer-title">{title}</div>}
      <div className="chart-renderer-content">
        {chartType === 'line' && (
          <LineChartRenderer data={data} xAxis={xAxis} series={series} title={title} />
        )}
        {chartType === 'bar' && (
          <BarChartRenderer data={data} xAxis={xAxis} series={series} title={title} />
        )}
        {chartType === 'pie' && (
          <PieChartRenderer data={data} title={title} />
        )}
        {chartType === 'area' && (
          <AreaChartRenderer data={data} xAxis={xAxis} series={series} title={title} />
        )}
        {chartType === 'scatter' && (
          <ScatterChartRenderer data={data} xAxis={xAxis} series={series} title={title} />
        )}
      </div>
    </div>
  );
});

ChartRenderer.displayName = 'ChartRenderer';

export default ChartRenderer;
